'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';

/**
 * A queue of user prompts the panel accepts WHILE the assistant is still
 * streaming. The textarea isn't blocked during a stream — submissions push
 * onto the queue and the head is flushed (dispatched as a real send) the
 * moment `isStreaming` flips false.
 *
 * The flush effect lives in this hook (not in the panel) because the guide
 * forbids raw `useEffect` in a component but allows the same effect when it
 * is isolated behind a clean hook API. The hook exposes:
 *   - `queue` (read-only count source for UI),
 *   - `enqueue(text)` to push during a stream,
 *   - `forceFlush()` to drain immediately (used by "send now" / stop).
 *
 * The flush trigger is genuinely the "stream finished" event surfaced via the
 * `isStreaming` transition — there's no Chat-level hook reachable from the
 * panel scope, so the subscription effect is the right escape hatch.
 */
export function usePendingPromptQueue(input) {
  const $ = _c(15);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [queue, setQueue] = useState(t0);
  let t1;
  if ($[1] !== input || $[2] !== queue) {
    t1 = () => {
      if (input.isStreaming) {
        return;
      }
      if (queue.length === 0) {
        return;
      }
      const [head, ...rest] = queue;
      if (!head) {
        return;
      }
      setQueue(rest);
      input.dispatch(head);
    };
    $[1] = input;
    $[2] = queue;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== input.isStreaming || $[5] !== queue) {
    t2 = [input.isStreaming, queue];
    $[4] = input.isStreaming;
    $[5] = queue;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  useEffect(t1, t2);
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = text => setQueue(q => q.length >= 2 ? q : [...q, text]);
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== queue) {
    t4 = () => {
      const snapshot = [...queue];
      setQueue([]);
      return snapshot;
    };
    $[8] = queue;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setQueue([]);
    t6 = index => setQueue(q_0 => q_0.filter((_, i) => i !== index));
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  let t7;
  if ($[12] !== queue || $[13] !== t4) {
    t7 = {
      queue,
      enqueue: t3,
      drain: t4,
      clear: t5,
      removeAt: t6
    };
    $[12] = queue;
    $[13] = t4;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}